import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { Navigate } from "react-router-dom";

type Tenant = {
  id: string;
  slug: string;
  display_name: string;
  protocol: string;
  supabase_provider: string;
  is_active: boolean;
  host_patterns: string[];
  allowed_email_domains: string[];
  allowed_roles: string[];
};

type Orphan = {
  id: string;
  attempted_email: string;
  subject: string | null;
  idp_tenant_id: string | null;
  occurred_at: string;
  resolved_at: string | null;
  resolution: string | null;
};

/**
 * Phase 0 admin shell for SSO configuration & orphan triage.
 * Full CRUD UI is intentionally minimal — this is a console scaffold.
 */
export default function SsoConsole() {
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [orphans, setOrphans] = useState<Orphan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [{ data: tRows }, { data: oRows }] = await Promise.all([
        supabase.from("idp_tenants" as any).select("*").order("display_name"),
        supabase.rpc("admin_list_orphan_sso_logins"),
      ]);
      setTenants((tRows as any) ?? []);
      setOrphans((oRows as any) ?? []);
      setLoading(false);
    })();
  }, []);

  if (adminLoading) return <div className="p-8">Loading…</div>;
  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <div className="max-w-5xl mx-auto p-8 space-y-10">
      <header>
        <h1 className="text-3xl font-bold mb-1">SSO / Identity Providers</h1>
        <p className="text-muted-foreground">
          Phase 0 — Microsoft Entra (OIDC) readiness. Admins and PM Tier 1 are blocked
          from SSO assignment by database constraint.
        </p>
      </header>

      <section>
        <h2 className="text-xl font-semibold mb-3">Identity providers</h2>
        {loading ? (
          <p className="text-muted-foreground">Loading tenants…</p>
        ) : tenants.length === 0 ? (
          <p className="text-muted-foreground">
            No identity providers configured. Local password login is the only sign-in path.
          </p>
        ) : (
          <div className="border rounded-md divide-y">
            {tenants.map((t) => (
              <div key={t.id} className="p-4 flex justify-between items-start">
                <div>
                  <div className="font-medium">{t.display_name} <span className="text-xs text-muted-foreground">({t.slug})</span></div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {t.protocol.toUpperCase()} · {t.supabase_provider} · hosts: {t.host_patterns.join(", ") || "—"} · domains: {t.allowed_email_domains.join(", ") || "—"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    allowed roles: {t.allowed_roles.join(", ")}
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${t.is_active ? "bg-green-100 text-green-800" : "bg-muted text-muted-foreground"}`}>
                  {t.is_active ? "Active" : "Inactive"}
                </span>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-3">
          Tenant CRUD UI lands in Phase 1. For now, configure rows via SQL.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">SSO orphan inbox</h2>
        {orphans.length === 0 ? (
          <p className="text-muted-foreground">No orphan sign-ins recorded.</p>
        ) : (
          <div className="border rounded-md divide-y">
            {orphans.map((o) => (
              <div key={o.id} className="p-4 flex justify-between items-center">
                <div>
                  <div className="font-medium">{o.attempted_email}</div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(o.occurred_at).toLocaleString()} ·
                    {o.resolution ? ` resolved: ${o.resolution}` : " unresolved"}
                  </div>
                </div>
                {!o.resolved_at && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      await supabase.rpc("admin_resolve_orphan", {
                        p_orphan_id: o.id,
                        p_resolution: "denied",
                        p_target_user_id: null,
                      });
                      setOrphans((prev) =>
                        prev.map((x) => (x.id === o.id ? { ...x, resolved_at: new Date().toISOString(), resolution: "denied" } : x))
                      );
                    }}
                  >
                    Deny
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
